import Box from '@mui/material/Box';
import Shimmer from './Shimmer';

// Skeleton for the v5 package detail page: breadcrumb, a full-width dark
// hero, then the long left column beside the sticky booking card.
//
// Rebuilt against the current layout — the previous version drew the
// pre-v5 page, which had a different hero and a different sidebar, so the
// page shifted when the row arrived.

// Blocks on the dark hero have to be lighter than it, not darker.
const onDark = { background: 'rgba(255,255,255,.1)' };

const Section = ({ children, bg = '#FFFFFF', gap = 2 }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap,
      minWidth: 0,
      p: 'clamp(20px, 3.4vw, 32px)',
      borderRadius: '28px',
      bgcolor: bg,
    }}
  >
    {children}
  </Box>
);

const PackageDetailShimmer = () => (
  <Box aria-label="Loading package">
    {/* breadcrumb */}
    <Box
      sx={{
        width: '100%', maxWidth: 1320, mx: 'auto', pt: 2.5, px: 3,
        display: 'flex', gap: 1, alignItems: 'center',
      }}
    >
      <Shimmer width={44} height={14} rounded={4} />
      <Shimmer width={8} height={14} rounded={4} />
      <Shimmer width={66} height={14} rounded={4} />
      <Shimmer width={8} height={14} rounded={4} />
      <Shimmer width={170} height={14} rounded={4} />
    </Box>

    {/* dark hero */}
    <Box sx={{ pt: 2, px: 2 }}>
      <Box
        sx={{
          width: '100%', maxWidth: 1320, mx: 'auto',
          display: 'flex', flexDirection: 'column', gap: 2.5,
          p: 'clamp(28px, 5vw, 56px)', borderRadius: '36px',
          bgcolor: 'var(--tl-g9)',
        }}
      >
        <Shimmer width={128} height={25} rounded={999} sx={onDark} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <Shimmer width="74%" height={48} rounded={10} sx={onDark} />
          <Shimmer width="44%" height={48} rounded={10} sx={onDark} />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Shimmer width="88%" height={16} rounded={4} sx={onDark} />
          <Shimmer width="62%" height={16} rounded={4} sx={onDark} />
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 140px), 1fr))',
            gap: 1, maxWidth: 760,
          }}
        >
          {[0, 1, 2, 3].map((i) => <Shimmer key={i} width="100%" height={74} rounded={18} sx={onDark} />)}
        </Box>
      </Box>
    </Box>

    <Box sx={{ width: '100%', maxWidth: 1320, mx: 'auto', px: 2, pt: 1.5, pb: 9 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
        {/* ── left column ────────────────────────────────────────────── */}
        <Box sx={{ flex: '2 1 560px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* what's included — the accordion */}
          <Section gap={1}>
            <Shimmer width="42%" height={34} rounded={8} sx={{ mb: 1 }} />
            {[0, 1, 2, 3, 4].map((i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  gap: 2, py: 2, borderTop: '1px solid #EEF3F1',
                }}
              >
                <Shimmer width={`${56 - i * 5}%`} height={18} rounded={4} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Shimmer width={52} height={14} rounded={4} />
                  <Shimmer width={30} height={30} circular />
                </Box>
              </Box>
            ))}
          </Section>

          {/* who it's for */}
          <Section>
            <Shimmer width="34%" height={34} rounded={8} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {[138, 102, 164, 120, 146, 96].map((w, i) => (
                <Shimmer key={i} width={w} height={38} rounded={999} />
              ))}
            </Box>
          </Section>

          {/* how to prepare — yellow */}
          <Section bg="var(--tl-yellow)" gap={1.75}>
            <Shimmer width="40%" height={34} rounded={8} sx={{ background: 'rgba(255,255,255,.65)' }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 1 }}>
              {[0, 1, 2].map((i) => (
                <Shimmer key={i} width="100%" height={104} rounded={18} sx={{ background: 'rgba(255,255,255,.65)' }} />
              ))}
            </Box>
          </Section>

          {/* compare packages */}
          <Section>
            <Shimmer width="46%" height={34} rounded={8} />
            <Shimmer width="70%" height={14} rounded={4} />
            {[0, 1, 2, 3].map((i) => (
              <Box
                key={i}
                sx={{
                  display: 'grid', gridTemplateColumns: '1.4fr repeat(3, 1fr)',
                  gap: 1, py: 1.5, alignItems: 'center',
                  ...(i > 0 ? { borderTop: '1px solid #EEF3F1' } : null),
                }}
              >
                <Shimmer width="80%" height={16} rounded={4} />
                <Shimmer width={28} height={16} rounded={4} sx={{ mx: 'auto' }} />
                <Shimmer width={28} height={16} rounded={4} sx={{ mx: 'auto' }} />
                <Shimmer width={28} height={16} rounded={4} sx={{ mx: 'auto' }} />
              </Box>
            ))}
          </Section>
        </Box>

        {/* ── sticky booking card ────────────────────────────────────── */}
        <Box
          component="aside"
          sx={{
            flex: '1 1 320px', minWidth: 0,
            display: 'flex', flexDirection: 'column', gap: 1.5,
            position: { md: 'sticky' }, top: { md: 100 },
          }}
        >
          <Section>
            <Shimmer width="56%" height={38} rounded={8} />
            {/* the people stepper */}
            <Shimmer width="100%" height={56} rounded={16} />
            <Shimmer width="86%" height={14} rounded={4} />
            {[0, 1, 2].map((i) => (
              <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
                <Shimmer width={112} height={15} rounded={4} />
                <Shimmer width={86} height={15} rounded={4} />
              </Box>
            ))}
            <Shimmer width="100%" height={54} rounded={16} />
            <Shimmer width="100%" height={54} rounded={16} />
            <Shimmer width="100%" height={88} rounded={16} />
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 0.75, pt: 1.75, borderTop: '1px solid #EEF3F1' }}>
              {[0, 1, 2].map((i) => <Shimmer key={i} width="100%" height={42} rounded={14} />)}
            </Box>
          </Section>
        </Box>
      </Box>
    </Box>
  </Box>
);

export default PackageDetailShimmer;
